import * as React from "react"
import Popover from "@mui/material/Popover"
import Button from "@mui/material/Button"
import Tooltip from "@mui/material/Tooltip"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"

import TagChip from "../TagChip"
import question_circle from "../../assets/images/question_circle.svg"
import red_down_icon from "../../assets/images/red_down_icon.svg"
import styles from "./tagsPopover.module.scss"
import CreatableTagsInput from "../CreatableTagsInput"
import { useDispatch, useSelector } from "react-redux"
import {
    useGetAllTagsQuery,
    useUnmapTagFromTicketMutation,
    useUpdatePrimaryTagMutation,
} from "../../pages/Common/commonService"
import { PrimaryTagButton } from "./PrimaryTagButton"
import { TagIcon } from "../CreatableTagsInput/TagIcon"
import { setTableFetchValue } from "../../pages/Finance/FinanceSlice"
import CircularLoader from "../Loader/CircularLoader"

type Tag = {
    id: number
    name: string
    color_code: string
    text_color_code: string
    created_at: string
    updated_at: string
    is_primary: number
    is_active: number
    is_visible: number
    created_by_id: number
    latest_updated_by_id: number
    created_by: string
    latest_updated_by: string
}

const TOOLTIP_TEXT =
    "Tags are used to categorize tickets. Later, filter your view using these tags for faster navigation."

export default function TagsPopover(props: any) {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    const [tags, setTags] = React.useState<Tag[]>([])
    const { data: tagsData } = useGetAllTagsQuery()
    const { user_id } = useSelector((store: any) => store.verifyLogin)
    const dispatch = useDispatch()

    React.useEffect(() => {
        if (!props.selectedTags) return

        let mergedTags = [...props.selectedTags]
        if (props.primaryTag && !props.selectedTags.find((t: Tag) => t.id === props.primaryTag.id)) {
            mergedTags.push(props.primaryTag)
        }

        const updatedTags = mergedTags.map((t: Tag) => ({
            ...t,
            is_primary: props.primaryTag?.id === t.id ? 1 : 0,
        }))

        setTags(updatedTags)
    }, [props.selectedTags, props.primaryTag])

    function lightenColor(hexColor: string, amount = 0.95) {
        if (!hexColor) return hexColor
        let col = hexColor.startsWith("#") ? hexColor.substring(1) : hexColor
        if (col.length === 3)
            col = col
                .split("")
                .map(c => c + c)
                .join("")
        const num = parseInt(col, 16)
        let r = (num >> 16) & 0xff,
            g = (num >> 8) & 0xff,
            b = num & 0xff
        r = Math.round(r + (255 - r) * amount)
        g = Math.round(g + (255 - g) * amount)
        b = Math.round(b + (255 - b) * amount)
        return `rgb(${r}, ${g}, ${b})`
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)
    const open = Boolean(anchorEl)
    const id = open ? "simple-popover" : undefined

    const [updateTagPrimaryStatus] = useUpdatePrimaryTagMutation()
    const [unmapTagFromTicket] = useUnmapTagFromTicketMutation()

    const handleDragStart = (tagId: number, from: "primary" | "other") => (event: React.DragEvent) => {
        event.dataTransfer.setData("tagId", tagId.toString())
        event.dataTransfer.setData("from", from)
    }

    const allowDrop = (event: React.DragEvent) => event.preventDefault()
    const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null)

    const handleDropToPrimary = async (event: React.DragEvent) => {
        event.preventDefault()
        const draggedTagId = Number(event.dataTransfer.getData("tagId"))
        const draggedTag = tags.find(tag => tag.id === draggedTagId)
        if (props?.tagAccess?.editTagAccess) {
            if (!draggedTag) return

            setTags(prev =>
                prev.map(tag => ({
                    ...tag,
                    is_primary: tag.id === draggedTagId ? 1 : 0,
                }))
            )

            dispatch(setTableFetchValue(false))
            try {
                await updateTagPrimaryStatus({
                    tag_id: draggedTag.id,
                    ticket_id: props.ticket_id,
                    module: props.module_name,
                    user_id,
                    view:
                        props.ticket_type === "Projects" || props.ticket_type === "Demolition" ? props?.view : "detail",
                    ticket_type: props.ticket_type,
                    is_primary: true,
                    tag_name: draggedTag.name,
                }).unwrap()
                dispatch(setTableFetchValue(true))
            } catch (error) {
                console.error("Failed to update primary tag:", error)
            }
        }
    }

    const handleDropToOther = (event: React.DragEvent, dropIndex?: number) => {
        event.preventDefault()
        if (props?.tagAccess?.editTagAccess) {
            const draggedTagId = Number(event.dataTransfer.getData("tagId"))
            const from = event.dataTransfer.getData("from")

            setTags(prevTags => {
                let newTags = [...prevTags]

                // If dragging from primary, set is_primary to 0
                if (from === "primary") {
                    newTags = newTags.map(tag => (tag.id === draggedTagId ? { ...tag, is_primary: 0 } : tag))
                }

                // Reorder if dropping within "other" list
                if (from === "other" && typeof dropIndex === "number") {
                    const draggedIndex = newTags.findIndex(tag => tag.id === draggedTagId)
                    const otherTags = newTags.filter(t => t.is_primary === 0)

                    const draggedTag = newTags[draggedIndex]
                    const fromIndexInOther = otherTags.findIndex(t => t.id === draggedTagId)

                    // Remove dragged tag from "other" list
                    const reordered = [...otherTags]
                    reordered.splice(fromIndexInOther, 1)
                    reordered.splice(dropIndex, 0, draggedTag)

                    // Recombine with primary tag
                    const primaryTags = newTags.filter(t => t.is_primary === 1)
                    return [...primaryTags, ...reordered]
                }

                return newTags
            })

            setDragOverIndex(null)
        }
    }

    const handleDragEnter = (index: number) => {
        setDragOverIndex(index)
    }

    const handleDragLeave = () => {
        setDragOverIndex(null)
    }

    const handleDelete = async (tagId: number, tagName: string) => {
        setTags(prev => prev.filter(tag => tag.id !== tagId))
        dispatch(setTableFetchValue(false))
        try {
            await unmapTagFromTicket({
                tag_id: tagId,
                ticket_id: props.ticket_id,
                tag_name: tagName,
                status: 0,
                module: props.module_name,
                ticket_type: props.ticket_type,
                view: props.ticket_type === "Projects" || props.ticket_type === "Demolition" ? props?.view : "detail",
                user_id,
            }).unwrap()
        } catch (error) {
            console.error("Failed to unmap tag:", error)
        } finally {
            dispatch(setTableFetchValue(true))
        }
    }

    const primaryTags = tags.filter(tag => tag.is_primary === 1)
    const otherTags = tags.filter(tag => tag.is_primary === 0)

    return (
        <>
            {props?.tableDisplay ? (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={"3px"}
                    {...(!props?.usedInNewTable ? { maxWidth: "200px" } : {})}>
                    {props?.primaryTag?.name && (
                        <Tooltip title={props?.primaryTag?.name}>
                            <Chip
                                icon={<TagIcon fill={props?.primaryTag?.color_code} />}
                                sx={{
                                    backgroundColor: lightenColor(props?.primaryTag?.color_code, 0.85),
                                    color: props?.primaryTag?.color_code,
                                    width: props?.usedInNewTable ? "calc(100% - 44px)" : "160px",
                                    height: "28px",
                                    justifyContent: "flex-start",
                                    fontWeight: 500,
                                    ".MuiChip-icon": {
                                        color: props?.primaryTag?.color_code,
                                    },
                                }}
                                label={props?.primaryTag?.name}
                                onClick={() => {}}
                                clickable={false}
                            />
                        </Tooltip>
                    )}
                    {props?.tagAccess?.viewTagAccess && (
                        <Chip
                            label={<img src={red_down_icon} alt="tags_arrow_icon" aria-describedby={id} />}
                            clickable={false}
                            onClick={handleClick}
                            sx={{
                                backgroundColor: "rgb(252, 87, 53, 0.15)",
                                display: "flex",
                                width: "44px",
                                height: "28px",
                                cursor: "pointer",
                            }}
                        />
                    )}
                </Stack>
            ) : props?.dealDetailDisplay ? (
                <div className={styles.dealDetailDisplay}>
                    <PrimaryTagButton tags={tags} />
                    {otherTags.length > 4 && (
                        <Tooltip
                            title={
                                <Box className={styles.tooltipContent}>
                                    {otherTags.slice(4).map(tag => (
                                        <Box
                                            key={tag.id}
                                            className={styles.tooltipItem}
                                            style={{ color: tag.color_code }}>
                                            <TagIcon fill={tag.color_code} />
                                            {tag.name}
                                        </Box>
                                    ))}
                                </Box>
                            }
                            placement="bottom-start"
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: "#fff",
                                        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                                        borderRadius: "6px",
                                        padding: "8px 12px",
                                        color: "#333",
                                        border: "none",
                                        fontSize: "12px",
                                        maxWidth: 220,
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: "#fff",
                                    },
                                },
                            }}>
                            <Box className={styles.tooltipBubble} sx={{ backgroundColor: lightenColor("#333", 0.7) }}>
                                +{otherTags.length - 4}
                            </Box>
                        </Tooltip>
                    )}
                    {props?.tagAccess?.viewTagAccess && (
                        <div aria-describedby={id} onClick={handleClick} className={styles.underline}>
                            {tags.length > 0 ? "Manage Tags" : "Add Tags"}
                        </div>
                    )}
                </div>
            ) : (
                props?.tagAccess?.viewTagAccess && (
                    <Button aria-describedby={id} variant="contained" onClick={handleClick}>
                        Open Popover
                    </Button>
                )
            )}

            {props?.tagAccess?.viewTagAccess && (
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    slotProps={{
                        paper: {
                            sx: {
                                padding: "12px 8px",
                                borderRadius: "8px",
                                boxShadow: "-2px -2px 4px rgba(0,0,0,0.05), 2px 2px 4px rgba(0,0,0,0.05)",
                            },
                        },
                    }}>
                    <Box className={styles.tagsPopoverBody}>
                        <Box className={`flex align-items-center gap-6 weight-500 ${styles.popoverHeader}`}>
                            Tags
                            <Tooltip
                                title={TOOLTIP_TEXT}
                                placement="bottom-start"
                                style={{ marginLeft: 2 }}
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: "190px",
                                        },
                                    },
                                }}>
                                <img src={question_circle} alt="question_circle" />
                            </Tooltip>
                        </Box>
                        {(props?.tagAccess?.editTagAccess || props?.tagAccess?.addTagAccess) && (
                            <Box>
                                <CreatableTagsInput
                                    allTagsOptions={tagsData?.data || []}
                                    tags={tags}
                                    setTags={setTags}
                                    ticket_id={props.ticket_id}
                                    module_name={props.module_name}
                                    ticket_type={props?.ticket_type}
                                    view={props?.view}
                                    addAccess={props?.tagAccess?.addTagAccess}
                                    mapAccess={props?.tagAccess?.editTagAccess}
                                />
                            </Box>
                        )}
                        {props?.isLoading ? (
                            <CircularLoader hide={!props?.isLoading} />
                        ) : (
                            <>
                                <Box
                                    className={`${styles.tagsContainer} ${styles.primaryTagContainer}`}
                                    onDrop={handleDropToPrimary}
                                    onDragOver={allowDrop}>
                                    <Box className="text-sm weight-500">Primary tag</Box>
                                    {primaryTags?.length ? (
                                        primaryTags.map(tag => (
                                            <TagChip
                                                key={tag.id}
                                                label={tag.name}
                                                textColor={tag.text_color_code}
                                                bgColor={tag.color_code}
                                                draggable
                                                tagAccess={props?.tagAccess}
                                                onDragStart={handleDragStart(tag.id, "primary")}
                                                onDelete={() => handleDelete(tag.id, tag.name)}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-xs opacity-50">No Primary tag</div>
                                    )}
                                </Box>
                                <Box className={`${styles.tagsContainer} ${styles.otherTagContainer}`}>
                                    <Box className={`${styles.otherTagHeader} text-sm weight-500`}>Other tags</Box>
                                    <Box className={`${styles.scrollableTagsList} d-grid w-100`}>
                                        {otherTags.map((tag, index) => (
                                            <Box
                                                key={tag.id}
                                                onDrop={e => handleDropToOther(e, index)}
                                                onDragOver={allowDrop}
                                                onDragEnter={() => handleDragEnter(index)}
                                                onDragLeave={handleDragLeave}
                                                className={dragOverIndex === index ? styles.dragOver : ""}>
                                                <TagChip
                                                    label={tag.name}
                                                    textColor={tag.text_color_code}
                                                    bgColor={tag.color_code}
                                                    draggable
                                                    onDragStart={handleDragStart(tag.id, "other")}
                                                    tagAccess={props?.tagAccess}
                                                    onDelete={() => handleDelete(tag.id, tag.name)}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Popover>
            )}
        </>
    )
}
